import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, addDoc, updateDoc, deleteDoc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { EditIcon, TrashIcon, CalendarIcon } from '../../components/icons/Icons';

// Helper for sort direction indicator
const SortIndicator = ({ direction }) => {
    if (!direction) return null;
    return direction === 'ascending' ? ' ▲' : ' ▼';
};

// Main component for the Holiday Management view
export default function HolidayManagement({ appId, canManage }) {
    const [holidays, setHolidays] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [holidayToEdit, setHolidayToEdit] = useState(null);
    // --- NEW STATE for filters ---
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [selectedLocation, setSelectedLocation] = useState('All');

    useEffect(() => {
        const holidaysRef = collection(db, `/artifacts/${appId}/public/data/holidays`);
        const q = query(holidaysRef, orderBy('date', 'desc'));
        const unsub = onSnapshot(q, (snapshot) => {
            setHolidays(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId]);

    // --- NEW: Dynamically generate year options for the filter ---
    const yearOptions = useMemo(() => {
        const years = new Set(holidays.map(h => h.date.toDate().getFullYear()));
        years.add(new Date().getFullYear()); // Ensure current year is always an option
        return Array.from(years).sort((a, b) => b - a);
    }, [holidays]);

    const handleOpenModal = (holiday = null) => {
        setHolidayToEdit(holiday);
        setIsModalOpen(true);
    };

    const handleDelete = async (holiday) => {
        // Replace window.confirm with a custom modal in a real app for better UX
        if (window.confirm(`Are you sure you want to delete the holiday "${holiday.holidayName}"?`)) {
            await deleteDoc(doc(db, `/artifacts/${appId}/public/data/holidays`, holiday.id));
        }
    };
    
    const filteredHolidays = useMemo(() => {
        return holidays.filter(holiday => {
            const holidayYear = holiday.date.toDate().getFullYear().toString();
            const yearMatch = selectedYear === 'All' || holidayYear === selectedYear;
            const locationMatch = selectedLocation === 'All' || holiday.location === selectedLocation;
            return yearMatch && locationMatch;
        });
    }, [holidays, selectedYear, selectedLocation]);

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                {/* --- NEW: Filter Section --- */}
                <div className="flex items-center space-x-4">
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        className="p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                        <option value="All">All Years</option>
                        {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                     <select
                        value={selectedLocation}
                        onChange={(e) => setSelectedLocation(e.target.value)}
                        className="p-2 border border-gray-300 rounded-md shadow-sm"
                    >
                        <option value="All">All Locations</option>
                        <option value="Global">Global</option>
                        <option value="Mumbai">Mumbai</option>
                        <option value="Delhi">Delhi</option>
                        <option value="Bangalore">Bangalore</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Melbourne">Melbourne</option>
                    </select>
                </div>
                {canManage &&
                    <button
                        onClick={() => handleOpenModal()}
                        className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 flex items-center"
                    >
                        <CalendarIcon />
                        <span className="ml-2">Add Holiday</span>
                    </button>
                }
            </div>
            {isLoading ? <LoadingSpinner /> : (
                <HolidayList
                    holidays={filteredHolidays} // Pass filtered holidays to the list
                    canManage={canManage}
                    onEdit={handleOpenModal}
                    onDelete={handleDelete}
                />
            )}
            {isModalOpen &&
                <HolidayModal
                    appId={appId}
                    existingHoliday={holidayToEdit}
                    onClose={() => setIsModalOpen(false)}
                />
            }
        </div>
    );
}

function HolidayList({ holidays, canManage, onEdit, onDelete }) {
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'ascending' });

    const sortedHolidays = useMemo(() => {
        let sortableItems = [...holidays];
        if (sortableItems.length > 0) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'date') {
                    aValue = aValue?.toDate() || 0;
                    bValue = bValue?.toDate() || 0;
                }

                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [holidays, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <div className="overflow-x-auto bg-white rounded-lg shadow border">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('date')}>
                            Date <SortIndicator direction={sortConfig.key === 'date' ? sortConfig.direction : null} />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Holiday Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        {canManage && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {sortedHolidays.length > 0 ? sortedHolidays.map(holiday => (
                        <tr key={holiday.id}>
                            <td className="px-6 py-4 whitespace-nowrap">{holiday.date.toDate().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</td>
                            <td className="px-6 py-4 whitespace-nowrap font-medium text-[#213368]">{holiday.holidayName}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{holiday.location}</td>
                            {canManage &&
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                        <button onClick={() => onEdit(holiday)} className="p-2 text-yellow-500 hover:text-yellow-700"><EditIcon /></button>
                                        <button onClick={() => onDelete(holiday)} className="p-2 text-red-500 hover:text-red-700"><TrashIcon /></button>
                                    </div>
                                </td>
                            }
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={canManage ? 4 : 3} className="text-center py-10 text-gray-500">
                                No holidays found for the selected filters.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function HolidayModal({ appId, existingHoliday, onClose }) {
    const [formData, setFormData] = useState({
        holidayName: existingHoliday?.holidayName || '',
        date: existingHoliday ? existingHoliday.date.toDate().toISOString().split('T')[0] : '',
        location: existingHoliday?.location || 'Global',
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        const dataToSave = {
            ...formData,
            date: Timestamp.fromDate(new Date(formData.date)),
        };

        try {
            if (existingHoliday) {
                await updateDoc(doc(db, `/artifacts/${appId}/public/data/holidays`, existingHoliday.id), dataToSave);
            } else {
                await addDoc(collection(db, `/artifacts/${appId}/public/data/holidays`), dataToSave);
            }
            onClose();
        } catch (error) {
            console.error("Error saving holiday:", error);
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
                <h2 className="text-xl font-bold text-[#213368] mb-4">{existingHoliday ? 'Edit' : 'Add'} Holiday</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="holidayName" value={formData.holidayName} onChange={handleChange} placeholder="Holiday Name" className="w-full p-2 border rounded-md" required />
                    <input name="date" type="date" value={formData.date} onChange={handleChange} className="w-full p-2 border rounded-md" required />
                    {/* --- UPDATED: Replaced text input with a select dropdown --- */}
                    <select
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        className="w-full p-2 border rounded-md bg-white"
                        required
                    >
                        <option value="Global">Global</option>
                        <option value="Mumbai">Mumbai</option>
                        <option value="Delhi">Delhi</option>
                        <option value="Bangalore">Bangalore</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Melbourne">Melbourne</option>
                    </select>
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={onClose} disabled={isSaving} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                        <button type="submit" disabled={isSaving} className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-blue-300">{isSaving ? 'Saving...' : 'Save Holiday'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
